import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { FiEye, FiEyeOff, FiArrowRight, FiX } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { BRAND, GOLD_GRADIENT, IVORY_GRADIENT_TEXT, Kicker, Segmented, inputStyle, labelStyle, buttonStyle } from '../common/ui';

function getErrorMessage(err) {
  const d = err?.response?.data;
  if (Array.isArray(d?.details) && d.details.length > 0) return d.details.map(e => e.message).join('\n');
  if (typeof d?.message === 'string') return d.message;
  if (Array.isArray(d?.errors) && d.errors[0]?.msg) return d.errors[0].msg;
  if (typeof d?.error === 'string') return d.error;
  return err?.message || 'Something went wrong. Try again!';
}

const panelVariants = {
  initial: { opacity: 0, x: 18 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.26, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, x: -14, transition: { duration: 0.18 } },
};
const subVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.22 } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.16 } },
};

const input = inputStyle(false);
const label = labelStyle(false);
const linkBtn = { background: 'none', border: 'none', cursor: 'pointer', fontFamily: BRAND.font, fontWeight: 700, fontSize: '0.86rem', color: BRAND.goldLight, padding: 0 };
const primary = (loading) => ({ ...buttonStyle('gold', 'lg'), width: '100%', marginTop: 6, cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.8 : 1 });

function EyeBtn({ shown, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-label={shown ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: 10, top: 10, background: 'none', border: 'none', cursor: 'pointer', color: BRAND.muted, fontSize: 18, padding: 4 }}>
      {shown ? <FiEyeOff /> : <FiEye />}
    </button>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginStudent, loginAdmin, registerAdmin } = useAuth();

  const codeFromLink = searchParams.get('code') || '';
  const startOnRegister = searchParams.get('register') === '1' || !!codeFromLink;
  const [mainTab, setMainTab] = useState(startOnRegister ? 'admin' : 'student');
  const [adminSub, setAdminSub] = useState(startOnRegister ? 'register' : 'login');

  const [displayName, setDisplayName] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [studentLoading, setStudentLoading] = useState(false);

  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminLoginLoading, setAdminLoginLoading] = useState(false);

  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const [helpOpen, setHelpOpen] = useState(false);
  const [helpName, setHelpName] = useState('');
  const [helpEmail, setHelpEmail] = useState('');
  const [helpMessage, setHelpMessage] = useState('');
  const [helpLoading, setHelpLoading] = useState(false);
  const [helpSent, setHelpSent] = useState(false);

  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regSchool, setRegSchool] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regCode, setRegCode] = useState(codeFromLink);
  const [regLoading, setRegLoading] = useState(false);
  // Invitation details resolved from the code: { email, school: { name } | null } | 'bad'
  const [invite, setInvite] = useState(null);
  const [inviteError, setInviteError] = useState('');

  // Look the invitation up as soon as the code looks complete (CORE-XXXX-XXXX)
  useEffect(() => {
    const clean = regCode.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (clean.length < 12) { setInvite(null); setInviteError(''); return; }
    let cancelled = false;
    api.get(`/auth/invite/${encodeURIComponent(regCode.trim())}`)
      .then(({ data }) => {
        if (cancelled) return;
        setInvite(data); setInviteError('');
        setRegEmail(data.email || '');
        if (data.school?.name) setRegSchool(data.school.name);
      })
      .catch((err) => { if (!cancelled) { setInvite('bad'); setInviteError(getErrorMessage(err)); } });
    return () => { cancelled = true; };
  }, [regCode]);
  const inviteOk = invite && invite !== 'bad';
  const schoolLocked = !!(inviteOk && invite.school?.name);

  const [showAdminPwd, setShowAdminPwd] = useState(false);
  const [showRegPwd, setShowRegPwd] = useState(false);

  // 'form' → 'gate' (logged in, welcome screen with the Enter button)
  const [phase, setPhase] = useState('form');
  const [gate, setGate] = useState({ route: '/dashboard', name: '' });

  const goToGate = useCallback((route, name) => { setGate({ route, name }); setPhase('gate'); }, []);

  // 'gate' → 'enter': the doors open — background rushes in, a gold ring flashes, navy fills the
  // screen, then the destination page irises in (EnterReveal in App.js reads the flag).
  const enterSchool = useCallback(() => {
    if (phase === 'enter') return;
    setPhase('enter');
    try { sessionStorage.setItem('core-enter', String(Date.now())); } catch { /* ignore */ }
    setTimeout(() => navigate(gate.route), 1050);
  }, [phase, gate.route, navigate]);

  const handleStudentSubmit = async (e) => {
    e.preventDefault();
    if (!displayName.trim() || !accessCode.trim()) { toast.error('Please enter your name and access code!'); return; }
    setStudentLoading(true);
    try {
      const u = await loginStudent(displayName.trim(), accessCode.trim());
      goToGate('/dashboard', u?.displayName || displayName.trim());
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setStudentLoading(false); }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    if (!adminEmail.trim() || !adminPassword) { toast.error('Please enter email and password!'); return; }
    setAdminLoginLoading(true);
    try {
      const u = await loginAdmin(adminEmail.trim(), adminPassword);
      goToGate('/admin', u?.name || 'Admin');
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setAdminLoginLoading(false); }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { toast.error('Please enter your email!'); return; }
    setForgotLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail.trim() });
      setForgotSent(true);
      toast.success('If that email exists, a reset link is on its way!');
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setForgotLoading(false); }
  };

  const handleHelpSubmit = async (e) => {
    e.preventDefault();
    if (!helpName.trim() || !helpEmail.trim() || helpMessage.trim().length < 10) { toast.error('Please fill in all fields (message: at least 10 characters).'); return; }
    setHelpLoading(true);
    try {
      await api.post('/support', { name: helpName.trim(), email: helpEmail.trim(), message: helpMessage.trim() });
      setHelpSent(true);
      toast.success('Request sent! We will get back to you soon.');
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setHelpLoading(false); }
  };

  const handleAdminRegister = async (e) => {
    e.preventDefault();
    if (!regCode.trim()) { toast.error('Enter the invitation code you received by email.'); return; }
    if (!regName.trim() || !regEmail.trim() || !regSchool.trim() || !regPassword) { toast.error('Please fill in all fields!'); return; }
    setRegLoading(true);
    try {
      const u = await registerAdmin({ name: regName.trim(), email: regEmail.trim(), password: regPassword, code: regCode.trim(), schoolName: regSchool.trim() });
      toast.success('Registration complete! Welcome!');
      goToGate('/admin', u?.name || regName.trim());
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setRegLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', position: 'relative', overflow: 'hidden', background: BRAND.navy900, fontFamily: BRAND.font, color: BRAND.ivory }}>
      <motion.img src={BRAND.bg.school} alt="" aria-hidden initial={{ scale: 1.06 }}
        animate={phase === 'enter' ? { scale: 1.42, filter: 'blur(6px) brightness(1.15)' } : { scale: 1, filter: 'blur(0px) brightness(1)' }}
        transition={phase === 'enter' ? { duration: 1.2, ease: [0.4, 0, 0.2, 1] } : { duration: 16, ease: 'linear' }}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 40%' }} />
      <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(7,20,45,0.96) 0%, rgba(7,20,45,0.8) 45%, rgba(7,20,45,0.3) 100%), linear-gradient(180deg, transparent 50%, rgba(7,20,45,0.9) 100%)' }} />

      {/* doors-open transition */}
      <AnimatePresence>
        {phase === 'enter' && (
          <motion.div key="enter-fx" aria-hidden initial={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', inset: 0, zIndex: 80, pointerEvents: 'none', display: 'grid', placeItems: 'center' }}>
            <motion.div initial={{ scale: 0.2, opacity: 0.95 }} animate={{ scale: 34, opacity: 0 }} transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
              style={{ width: 120, height: 120, borderRadius: '50%', border: '3px solid rgba(226,184,120,0.9)', boxShadow: '0 0 60px rgba(205,155,80,0.6)' }} />
            <motion.div initial={{ scale: 0.4, opacity: 0.8 }} animate={{ scale: 30, opacity: 0 }} transition={{ duration: 1.1, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
              style={{ position: 'absolute', width: 120, height: 120, borderRadius: '50%', border: '2px solid rgba(249,240,231,0.6)' }} />
            <motion.img src={BRAND.owl} alt="" initial={{ scale: 1, opacity: 1 }} animate={{ scale: 2.6, opacity: 0 }} transition={{ duration: 0.9, ease: 'easeIn' }}
              style={{ position: 'absolute', width: 120, filter: 'drop-shadow(0 0 40px rgba(205,155,80,0.8))' }} />
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.45, delay: 0.55 }} style={{ position: 'absolute', inset: 0, background: BRAND.navy900 }} />
          </motion.div>
        )}
      </AnimatePresence>

      <div style={{ position: 'relative', zIndex: 1, minHeight: '100vh', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,460px)', gap: 'clamp(24px,4vw,64px)', alignItems: 'center', padding: 'clamp(20px,4vw,64px)', maxWidth: 1280, margin: '0 auto' }} className="core-login-grid">
        <style>{`@media (max-width: 900px){ .core-login-grid{ grid-template-columns: 1fr !important; } .core-login-hero{ display:none !important; } }`}</style>

        {/* left: brand copy */}
        <motion.div className="core-login-hero" initial={{ opacity: 0, y: 20 }} animate={phase === 'enter' ? { opacity: 0, y: -24, scale: 0.96 } : { opacity: 1, y: 0, scale: 1 }} transition={{ duration: phase === 'enter' ? 0.45 : 0.6 }} style={{ maxWidth: 560 }}>
          <Kicker>C.O.R.E. for Schools</Kicker>
          <h1 style={{ fontSize: 'clamp(2.4rem,5vw,4rem)', lineHeight: 1.02, fontWeight: 900, margin: '14px 0 14px', ...IVORY_GRADIENT_TEXT }}>Learn to see<br />the trap first.</h1>
          <p style={{ fontFamily: BRAND.serif, fontStyle: 'italic', fontSize: 'clamp(1.15rem,1.8vw,1.55rem)', lineHeight: 1.3, color: 'rgba(249,240,231,0.88)' }}>
            Cyber Operational Risk Education — 18 cinematic lessons with Trace, Mina and the team, for grades 6–7.
          </p>
          <div style={{ display: 'flex', gap: 32, marginTop: 40 }}>
            {[['18', 'lessons'], ['90+', 'missions'], ['100', 'badges to earn']].map(([n, l]) => (
              <div key={l}><b style={{ display: 'block', fontSize: '1.5rem', color: BRAND.goldLight, lineHeight: 1 }}>{n}</b><span style={{ fontSize: '0.72rem', color: BRAND.muted, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700 }}>{l}</span></div>
            ))}
          </div>
        </motion.div>

        {/* right: card */}
        <AnimatePresence mode="wait">
          {phase === 'form' ? (
            <motion.div key="card" initial={{ opacity: 0, y: 28, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.25 } }} transition={{ type: 'spring', stiffness: 100, damping: 18 }}
              style={{ width: '100%', background: 'rgba(11,26,58,0.74)', border: `1px solid ${BRAND.line}`, borderRadius: 26, backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', boxShadow: '0 24px 70px rgba(0,0,0,0.5)', padding: 'clamp(22px,3vw,34px)' }}>
              <img src={BRAND.owl} alt="C.O.R.E." style={{ width: 84, margin: '0 auto 10px', display: 'block', filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.5))' }} />
              <h2 style={{ textAlign: 'center', fontWeight: 800, fontSize: '1.5rem', margin: 0 }}>Welcome back</h2>
              <p style={{ textAlign: 'center', color: BRAND.muted, fontSize: '0.92rem', margin: '4px 0 20px' }}>Choose how you want to enter</p>

              <Segmented value={mainTab} onChange={setMainTab} fill style={{ marginBottom: 20 }}
                options={[{ value: 'student', label: 'Student' }, { value: 'admin', label: 'Teacher / Admin' }]} />

              <AnimatePresence mode="wait">
                {mainTab === 'student' && (
                  <motion.form key="student" variants={panelVariants} initial="initial" animate="animate" exit="exit" onSubmit={handleStudentSubmit}>
                    <label style={label}>Your name</label>
                    <input type="text" autoComplete="nickname" placeholder="What's your name?" value={displayName} onChange={e => setDisplayName(e.target.value)} style={{ ...input, marginBottom: 14 }} disabled={studentLoading} />
                    <label style={label}>Access code</label>
                    <input type="text" autoComplete="off" placeholder="The code your teacher gave you" value={accessCode} onChange={e => setAccessCode(e.target.value)} style={{ ...input, marginBottom: 14 }} disabled={studentLoading} />
                    <motion.button type="submit" disabled={studentLoading} whileHover={{ scale: studentLoading ? 1 : 1.02 }} whileTap={{ scale: studentLoading ? 1 : 0.98 }} style={primary(studentLoading)}>
                      {studentLoading ? 'One moment…' : <>Enter the School <FiArrowRight size={18} /></>}
                    </motion.button>
                  </motion.form>
                )}

                {mainTab === 'admin' && (
                  <motion.div key="admin" variants={panelVariants} initial="initial" animate="animate" exit="exit">
                    {adminSub !== 'forgot' && (
                      <div style={{ display: 'flex', gap: 18, marginBottom: 16, borderBottom: `1px solid ${BRAND.line}` }}>
                        {[['login', 'Log in'], ['register', 'Register']].map(([k, l]) => (
                          <button key={k} type="button" onClick={() => setAdminSub(k)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: BRAND.font, fontWeight: 800, fontSize: '0.9rem', padding: '6px 2px 10px', color: adminSub === k ? BRAND.ivory : BRAND.muted, borderBottom: `2px solid ${adminSub === k ? BRAND.gold : 'transparent'}`, marginBottom: -1 }}>{l}</button>
                        ))}
                      </div>
                    )}

                    <AnimatePresence mode="wait">
                      {adminSub === 'login' && (
                        <motion.form key="adm-login" variants={subVariants} initial="initial" animate="animate" exit="exit" onSubmit={handleAdminLogin}>
                          <label style={label}>Email</label>
                          <input type="email" autoComplete="email" placeholder="name@school.com" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} style={{ ...input, marginBottom: 14 }} disabled={adminLoginLoading} />
                          <label style={label}>Password</label>
                          <div style={{ position: 'relative', marginBottom: 14 }}>
                            <input type={showAdminPwd ? 'text' : 'password'} autoComplete="current-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder="••••••••" value={adminPassword} onChange={e => setAdminPassword(e.target.value)} style={{ ...input, paddingRight: 44 }} disabled={adminLoginLoading} />
                            <EyeBtn shown={showAdminPwd} onClick={() => setShowAdminPwd(v => !v)} />
                          </div>
                          <motion.button type="submit" disabled={adminLoginLoading} whileHover={{ scale: adminLoginLoading ? 1 : 1.02 }} whileTap={{ scale: adminLoginLoading ? 1 : 0.98 }} style={primary(adminLoginLoading)}>
                            {adminLoginLoading ? 'Logging in…' : 'Log in'}
                          </motion.button>
                          <div style={{ textAlign: 'center', marginTop: 14 }}>
                            <button type="button" onClick={() => { setForgotSent(false); setAdminSub('forgot'); }} style={linkBtn}>Forgot your password?</button>
                          </div>
                        </motion.form>
                      )}

                      {adminSub === 'forgot' && (
                        <motion.div key="adm-forgot" variants={subVariants} initial="initial" animate="animate" exit="exit">
                          {forgotSent ? (
                            <div style={{ textAlign: 'center', padding: '8px 4px' }}>
                              <div style={{ fontSize: '2rem', marginBottom: 8 }}>📬</div>
                              <p style={{ fontWeight: 800, marginBottom: 6 }}>Check your inbox</p>
                              <p style={{ fontSize: '0.9rem', color: BRAND.muted, lineHeight: 1.5 }}>If an account with that email exists, we've sent a link to reset your password. The link expires in 1 hour.</p>
                            </div>
                          ) : (
                            <form onSubmit={handleForgotSubmit}>
                              <p style={{ fontSize: '0.9rem', color: BRAND.muted, marginBottom: 14, lineHeight: 1.5 }}>Enter the email of your administrator account and we'll send you a link to reset your password.</p>
                              <label style={label}>Email</label>
                              <input type="email" autoComplete="email" placeholder="name@school.com" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} style={{ ...input, marginBottom: 14 }} disabled={forgotLoading} />
                              <motion.button type="submit" disabled={forgotLoading} whileHover={{ scale: forgotLoading ? 1 : 1.02 }} whileTap={{ scale: forgotLoading ? 1 : 0.98 }} style={primary(forgotLoading)}>
                                {forgotLoading ? 'Sending…' : 'Send reset link'}
                              </motion.button>
                            </form>
                          )}
                          <div style={{ textAlign: 'center', marginTop: 16 }}>
                            <button type="button" onClick={() => setAdminSub('login')} style={linkBtn}>← Back to login</button>
                          </div>
                        </motion.div>
                      )}

                      {adminSub === 'register' && (
                        <motion.form key="adm-reg" variants={subVariants} initial="initial" animate="animate" exit="exit" onSubmit={handleAdminRegister}>
                          <p style={{ fontSize: '0.86rem', color: BRAND.muted, margin: '0 0 12px', lineHeight: 1.5 }}>Teacher accounts are created by invitation. Enter the personal code you received from CORE.</p>
                          <label style={label}>Invitation code</label>
                          <input type="text" autoComplete="off" placeholder="CORE-XXXX-XXXX" value={regCode} onChange={e => setRegCode(e.target.value)} spellCheck={false}
                            style={{ ...input, marginBottom: inviteError || inviteOk ? 6 : 14, fontFamily: 'Menlo, Consolas, monospace', letterSpacing: 1, textTransform: 'uppercase', borderColor: invite === 'bad' ? BRAND.danger : inviteOk ? BRAND.success : undefined }} disabled={regLoading} />
                          {invite === 'bad' && <p style={{ fontSize: '0.8rem', color: BRAND.danger, margin: '0 0 12px', fontWeight: 700 }}>{inviteError}</p>}
                          {inviteOk && <p style={{ fontSize: '0.8rem', color: BRAND.success, margin: '0 0 12px', fontWeight: 700 }}>✓ Invitation for {invite.email}{invite.school?.name ? ` · ${invite.school.name}` : ''}</p>}
                          <label style={label}>Your name</label>
                          <input type="text" autoComplete="name" placeholder="First and last name" value={regName} onChange={e => setRegName(e.target.value)} style={{ ...input, marginBottom: 14 }} disabled={regLoading} />
                          <label style={label}>School email</label>
                          <input type="email" autoComplete="email" placeholder="name@school.k12.oh.us" value={regEmail} onChange={e => setRegEmail(e.target.value)} readOnly={!!inviteOk} style={{ ...input, marginBottom: 14, opacity: inviteOk ? 0.75 : 1 }} disabled={regLoading} />
                          <label style={label}>School name</label>
                          <input type="text" autoComplete="organization" placeholder="e.g. Tipp City Middle School" value={regSchool} onChange={e => setRegSchool(e.target.value)} readOnly={schoolLocked} style={{ ...input, marginBottom: 14, opacity: schoolLocked ? 0.75 : 1 }} disabled={regLoading} />
                          <label style={label}>Password</label>
                          <div style={{ position: 'relative', marginBottom: 14 }}>
                            <input type={showRegPwd ? 'text' : 'password'} autoComplete="new-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder="At least 12 characters, upper/lower case and a digit" value={regPassword} onChange={e => setRegPassword(e.target.value)} style={{ ...input, paddingRight: 44 }} disabled={regLoading} />
                            <EyeBtn shown={showRegPwd} onClick={() => setShowRegPwd(v => !v)} />
                          </div>
                          <motion.button type="submit" disabled={regLoading} whileHover={{ scale: regLoading ? 1 : 1.02 }} whileTap={{ scale: regLoading ? 1 : 0.98 }} style={primary(regLoading)}>
                            {regLoading ? 'Creating your account…' : 'Create teacher account'}
                          </motion.button>
                        </motion.form>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}
              </AnimatePresence>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18, fontSize: '0.84rem' }}>
                <Link to="/" style={{ fontWeight: 700, color: BRAND.muted }}>← Back to home</Link>
                <button type="button" onClick={() => { setHelpSent(false); setHelpOpen(true); }} style={linkBtn}>Need help?</button>
              </div>
            </motion.div>
          ) : phase === 'enter' ? null : (
            <motion.div key="gate" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: -10, transition: { duration: 0.35 } }} transition={{ duration: 0.5 }}
              style={{ width: '100%', textAlign: 'center', background: 'rgba(11,26,58,0.74)', border: `1px solid ${BRAND.line}`, borderRadius: 26, backdropFilter: 'blur(16px)', boxShadow: '0 24px 70px rgba(0,0,0,0.5)', padding: 'clamp(28px,4vw,44px) clamp(22px,3vw,34px)' }}>
              <motion.img src={BRAND.owl} alt="" initial={{ scale: 0.7, rotate: -8 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 120, damping: 12 }} style={{ width: 120, margin: '0 auto 14px', display: 'block', filter: 'drop-shadow(0 10px 30px rgba(205,155,80,0.45))' }} />
              <Kicker>Access granted</Kicker>
              <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 900, margin: '10px 0 6px', ...IVORY_GRADIENT_TEXT }}>Welcome, {gate.name || 'friend'}.</h2>
              <p style={{ fontFamily: BRAND.serif, fontStyle: 'italic', fontSize: '1.2rem', color: 'rgba(249,240,231,0.85)', marginBottom: 26 }}>Trace and the team are waiting inside.</p>
              <motion.button type="button" onClick={enterSchool} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} style={{ ...buttonStyle('gold', 'lg'), fontSize: '1.1rem', padding: '17px 40px' }}>
                Enter the School <FiArrowRight size={20} />
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* help modal */}
      <AnimatePresence>
        {phase === 'form' && helpOpen && (
          <motion.div key="help" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setHelpOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(7,20,45,0.72)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
            <motion.div initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.94 }} transition={{ type: 'spring', stiffness: 120, damping: 18 }} onClick={e => e.stopPropagation()}
              style={{ width: '100%', maxWidth: 440, maxHeight: '90vh', overflowY: 'auto', borderRadius: 24, padding: '24px', background: BRAND.navy800, border: `1px solid ${BRAND.line}`, boxShadow: '0 24px 70px rgba(0,0,0,0.5)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h2 style={{ fontSize: '1.2rem', fontWeight: 900, margin: 0 }}>Ask for help</h2>
                <button type="button" onClick={() => setHelpOpen(false)} aria-label="Close" style={{ background: 'rgba(249,240,231,0.08)', border: `1px solid ${BRAND.line}`, borderRadius: 10, width: 34, height: 34, display: 'grid', placeItems: 'center', cursor: 'pointer', color: BRAND.ivory }}><FiX /></button>
              </div>
              {helpSent ? (
                <div style={{ textAlign: 'center', padding: '12px 4px' }}>
                  <div style={{ fontSize: '2rem', marginBottom: 8 }}>✅</div>
                  <p style={{ fontWeight: 800, marginBottom: 6 }}>Request sent!</p>
                  <p style={{ fontSize: '0.9rem', color: BRAND.muted, lineHeight: 1.5, marginBottom: 16 }}>Our team has received your message and will reply to your email as soon as possible.</p>
                  <button type="button" onClick={() => setHelpOpen(false)} style={buttonStyle('gold')}>Close</button>
                </div>
              ) : (
                <form onSubmit={handleHelpSubmit}>
                  <p style={{ fontSize: '0.88rem', color: BRAND.muted, marginBottom: 14, lineHeight: 1.5 }}>Having trouble logging in or using the platform? Describe the problem and our team will get back to you by email.</p>
                  <label style={label}>Your name</label>
                  <input type="text" autoComplete="name" placeholder="Your name" value={helpName} onChange={e => setHelpName(e.target.value)} style={{ ...input, marginBottom: 14 }} disabled={helpLoading} />
                  <label style={label}>Your email</label>
                  <input type="email" autoComplete="email" placeholder="name@school.com" value={helpEmail} onChange={e => setHelpEmail(e.target.value)} style={{ ...input, marginBottom: 14 }} disabled={helpLoading} />
                  <label style={label}>What's the problem?</label>
                  <textarea placeholder="Describe your issue…" value={helpMessage} onChange={e => setHelpMessage(e.target.value)} disabled={helpLoading} rows={4} maxLength={2000} style={{ ...input, resize: 'vertical', minHeight: 90, marginBottom: 14 }} />
                  <motion.button type="submit" disabled={helpLoading} whileHover={{ scale: helpLoading ? 1 : 1.02 }} whileTap={{ scale: helpLoading ? 1 : 0.98 }} style={primary(helpLoading)}>
                    {helpLoading ? 'Sending…' : 'Send request'}
                  </motion.button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
